import { ElementRef, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatPaginator, MatTableDataSource } from '@angular/material';
import { combineLatest, merge, Subject } from 'rxjs';
import { first, map, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApplicationService } from 'services/application/application.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';
import { UnsavedChangesService } from 'routes/routes.guard';
import { TitleCasePipe } from 'pipes/titlecase.pipe';
var CategoriesComponent = /** @class */ (function () {
    function CategoriesComponent(appService, dialogService, errorService, fb, screenSizeService, splashService, titleCasePipe, unsavedChangesService, userService) {
        this.appService = appService;
        this.dialogService = dialogService;
        this.errorService = errorService;
        this.fb = fb;
        this.screenSizeService = screenSizeService;
        this.splashService = splashService;
        this.titleCasePipe = titleCasePipe;
        this.unsavedChangesService = unsavedChangesService;
        this.userService = userService;
        this._subrelease = new Subject();
        this.AllVisibleHeaders = ['InUse', 'CategoryDescription', 'LastName', 'Actions'];
    }
    CategoriesComponent.prototype.ngOnDestroy = function () {
        this._subrelease.next();
        this._subrelease.complete();
        // Stop tracking.
        this.TakeDownTracking();
    };
    CategoriesComponent.prototype.ngOnInit = function () {
        var self = this;
        self.AllCabinetMembers = [];
        self.Categories = [];
        self.CategoriesSource = new MatTableDataSource(self.Categories);
        self.CategoriesSource.paginator = self.paginator;
        self.CategoryPanelShowing = false;
        self.NoData = true;
        self.User = null;
        self.ViewReady = false;
        // Make the new category form
        self.CategoryForm = self.fb.group({
            Description: ['', Validators.required],
            CabinetMember: [null, Validators.required],
            Categories: [null],
        });
        self.CategoryForm.valueChanges
            .pipe(takeUntil(self._subrelease), distinctUntilChanged(), debounceTime(250))
            .subscribe(function (value) {
            // If any of the form array contents is flagged as Changed, or ToRemove, we have pending changes.
            var arrchanged = (value.Categories || []).filter(function (c) { return c.Changed === true || c.ToRemove === true; });
            self.HasAnyChanges = !!arrchanged.length;
        });
        // Subscribe to changes in the screen size.
        self.screenSizeService.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(function (size) {
            self.SmallDisplay = size.IsSmallSize;
            self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllVisibleHeaders.slice();
            self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
        });
        self.SmallDisplay = self.screenSizeService.ScreenSize.IsSmallSize; // Initial setting
        // Wait for the first non-null UserInfo object, to begin component life cycle.
        self.userService.UserSubject.pipe(takeUntil(self._subrelease), first(function (_u) { return _u && !!_u.AuthToken; })).subscribe(function (_user) {
            self.User = _user;
            // Set up constant user monitoring
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(function (user) {
                self.User = user;
            }, function (error) { return self.errorService.HandleServiceError(error); });
            // Grab the information needed for the component
            combineLatest(self.appService.GetAllCategories(), self.appService.GetAllCabinetMembers())
                .pipe(takeUntil(self._subrelease), map(function (_packet) {
                return {
                    Categories: _packet[0],
                    CabinetMembers: _packet[1],
                };
            }))
                .subscribe(function (packet) {
                self.AllCabinetMembers = packet.CabinetMembers || [];
                // Apply category setup
                self.ApplyCategorySetup(packet.Categories || []);
                // Setup change tracking.
                self.SetupTracking();
                // The page is ready for viewing.
                self.ViewReady = true;
            }, function (error) { return self.errorService.HandleServiceError(error); });
        }, function (error) { return self.errorService.HandleServiceError(error); });
    };
    // #region Add Category
    CategoriesComponent.prototype.AddCategory = function () {
        var self = this;
        var formval = self.CategoryForm.value;
        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
            TitleText: 'Add New Category Confirmation',
            MessageText: "You are about to add the following new category to the IRIS System:<br /><br />" +
                ("<strong>Description:</strong> " + formval.Description + "<br />") +
                ("<strong>Cabinet Member:</strong> " + self.titleCasePipe.transform(formval.CabinetMember.FirstName) + " ") +
                (self.titleCasePipe.transform(formval.CabinetMember.LastName) + " (" + formval.CabinetMember.LocalJobTitle + ")<br /><br />") +
                "Once added, this new category will become available to IRIS facilitators for use in the IRIS System. " +
                "Are you sure you wish to add this new category?",
            OKText: 'Yes - Add Category',
            CancelText: 'No - Cancel',
        })
            .subscribe(function (result) {
            if (result === true) {
                // Add the new category by inserting a new record in the categories form array
                var maxsort = (formval.Categories || []).reduce(function (p, c) { return (p > c.SortOrder ? p : c.SortOrder); }, -1);
                // Have to find where in the alphabetic order this entry should go in the form array
                var farr = self.CategoryForm.get('Categories');
                var farrval = farr.value;
                var insertIdx = farrval.reduce(function (p, c, idx) { return (c.CategoryDescription.localeCompare(formval.Description) < 0 && idx > p ? idx : p); }, -1);
                var fentry = self.fb.group({
                    CategoryID: [null],
                    CategoryDescription: [formval.Description, Validators.required],
                    InUse: [false],
                    SortOrder: [maxsort + 1],
                    Changed: [true],
                    CabinetMember: [formval.CabinetMember, Validators.required],
                    OriginalCabinetMember: [null],
                    OriginalCategoryDescription: [''],
                    ToRemove: [false],
                });
                // Add to form array at the index found (if less than the maximum), or push to the end
                if (insertIdx < farrval.length - 1) {
                    farr.insert(insertIdx + 1, fentry);
                }
                else {
                    farr.push(fentry);
                }
                // Toast entry
                self.splashService.SnackbarSuccess('New Category Queued for Saving');
                // Clear/reset
                self.ResetForm();
            }
        });
    };
    // #endregion
    // #region Apply Category Setup
    CategoriesComponent.prototype.ApplyCategorySetup = function (cats) {
        var self = this;
        // If we have data, parse through and for each find the entry from AlLCabinetMembers and attach it in place of the provided one.
        var cararr = [];
        (cats || []).forEach(function (c) {
            var fentry = self.fb.group({
                CategoryID: [c.CategoryID],
                CategoryDescription: [c.CategoryDescription, Validators.required],
                InUse: [c.InUse],
                SortOrder: [c.SortOrder],
                Changed: [false],
                CabinetMember: [
                    c.CabinetMember ? self.AllCabinetMembers.find(function (cb) { return cb.SAPID === c.CabinetMember.SAPID; }) : null,
                    Validators.required,
                ],
                OriginalCabinetMember: [
                    c.CabinetMember ? self.AllCabinetMembers.find(function (cb) { return cb.SAPID === c.CabinetMember.SAPID; }) : null,
                ],
                OriginalCategoryDescription: [c.CategoryDescription],
                ToRemove: [false],
            });
            // Subscribe for changes
            merge(fentry.get('CabinetMember').valueChanges, fentry.get('CategoryDescription').valueChanges)
                .pipe(takeUntil(self._subrelease), distinctUntilChanged(), debounceTime(250))
                .subscribe(function (val) {
                // Set the changed flag.
                var changed = false;
                var value = fentry.value;
                if (!changed && value.OriginalCategoryDescription !== value.CategoryDescription) {
                    changed = true;
                }
                if (!changed &&
                    !!value.CabinetMember &&
                    !!value.OriginalCabinetMember &&
                    value.CabinetMember.SAPID !== value.OriginalCabinetMember.SAPID) {
                    changed = true;
                }
                if (!changed && !!value.CabinetMember && !value.OriginalCabinetMember) {
                    changed = true;
                }
                if (!changed && !value.CabinetMember && !!value.OriginalCabinetMember) {
                    changed = true;
                }
                if (!changed && value.ToRemove) {
                    changed = true;
                }
                fentry.get('Changed').setValue(changed);
            });
            cararr.push(fentry);
        });
        self.CategoriesFArray = self.fb.array(cararr);
        self.CategoryForm.setControl('Categories', self.CategoriesFArray);
        self.Categories = self.CategoriesFArray.value;
        self.NoData = !self.Categories.length;
        self.CategoriesSource.data = self.NoData ? [{}] : self.Categories;
        // Update column displays
        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllVisibleHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    };
    // #endregion
    // #region Can Deactivate
    CategoriesComponent.prototype.CanDeactivate = function (liveCheck) {
        var self = this;
        if (!self.CategoryForm) {
            return true;
        }
        // Are there unsaved changes?
        if (!self.HasAnyChanges) {
            // No. The user may deactivate.
            return true;
        }
        // If this is a live check (native from browser), forego the confirmation dialog as the native browser will catch instead.
        if (liveCheck === true) {
            return false;
        }
        // Need to prompt
        return self.dialogService.OpenConfirmationDialog({
            TitleText: 'Unsaved Changes Detected',
            MessageText: "The categories listing below has some unsaved changes (noted with a pink highlight) which have not " +
                "yet been saved / submitted to the system. If you leave this page now, your unsaved " +
                "changes will be lost. Do you wish to discard your changes and leave the page?",
            OKText: 'Yes - Discard Changes',
            CancelText: 'No - Stay On Page',
        });
    };
    // #endregion
    // #region Remove Category
    CategoriesComponent.prototype.RemoveCategory = function (catfg, idx) {
        var self = this;
        if (!catfg) {
            return;
        }
        var cat = catfg.value;
        // Get a confirmation. The style of confirmation will depend on whether or not this is an existing entry, or one which is pending to
        // be added and the user changed their mind.
        self.dialogService
            .OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: "You are about to remove the following category from the IRIS System:<br /><br />" +
                ("<strong>Description: </strong>" + cat.CategoryDescription + "<br />") +
                ("<strong>Cabinet First Name: </strong>" + (cat.CabinetMember ? self.titleCasePipe.transform(cat.CabinetMember.FirstName) : '') + "<br />") +
                ("<strong>Cabinet Last Name: </strong>" + (cat.CabinetMember ? self.titleCasePipe.transform(cat.CabinetMember.LastName) : '') + "<br />") +
                ("<strong>Cabinet SAP #: </strong>" + (cat.CabinetMember ? cat.CabinetMember.SAPID : '') + "<br />") +
                ("<strong>Cabinet Title: </strong>" + (cat.CabinetMember ? cat.CabinetMember.LocalJobTitle : '') + "<br /><br />") +
                "Once removed, the category will no longer be an option in IRIS for facilitator use. Are you " +
                "sure you wish to continue?",
            OKText: 'Yes - Remove',
            CancelText: 'No - Cancel',
        })
            .subscribe(function (result) {
            if (result === true) {
                var farr = self.CategoryForm.get('Categories');
                // If the entry being removed has no associated CategoryID, it is a new pending entry and we can remove that with
                // no extra work - just yank it from the form array.
                if (cat.CategoryID == null) {
                    // Remove outright.
                    farr.removeAt(idx);
                    // Splash success.
                    self.splashService.SnackbarSuccess('Category removed successfully.');
                }
                else {
                    // This must be queued for removing. Set the flag for now, which will be caught up later in the mass update.
                    catfg.get('ToRemove').setValue(true);
                    // Splash alert.
                    self.splashService.SnackbarSuccess('Category Queued for Removal Successfully.');
                }
            }
        });
    };
    // #endregion
    // #region Reset Category
    CategoriesComponent.prototype.ResetCategory = function (cat) {
        if (!cat) {
            return;
        }
        // Replace the existing category description and cabinet member with the original entries stored.
        var formval = cat.value;
        cat.patchValue({
            CategoryDescription: formval.OriginalCategoryDescription,
            CabinetMember: formval.OriginalCabinetMember,
            Changed: false,
        });
    };
    // #endregion
    // #region Reset Form
    CategoriesComponent.prototype.ResetForm = function () {
        var self = this;
        self.CategoryForm.patchValue({
            Description: '',
            CabinetMember: null,
        }, { emitEvent: false });
        self.CategoryPanelShowing = false;
    };
    // #endregion
    // #region Restore Category
    CategoriesComponent.prototype.RestoreCategory = function (catfg) {
        var self = this;
        if (!catfg) {
            return;
        }
        // Get confirmation
        var cat = catfg.value;
        self.dialogService
            .OpenConfirmationDialog({
            TitleText: 'Restore Category Pending Removal?',
            MessageText: "You are about to restore the following category that is marked for removal in the IRIS System:<br /><br />" +
                ("<strong>Description: </strong>" + cat.CategoryDescription + "<br />") +
                ("<strong>Cabinet First Name: </strong>" + (cat.CabinetMember ? self.titleCasePipe.transform(cat.CabinetMember.FirstName) : '') + "<br />") +
                ("<strong>Cabinet Last Name: </strong>" + (cat.CabinetMember ? self.titleCasePipe.transform(cat.CabinetMember.LastName) : '') + "<br />") +
                ("<strong>Cabinet SAP #: </strong>" + (cat.CabinetMember ? cat.CabinetMember.SAPID : '') + "<br />") +
                ("<strong>Cabinet Title: </strong>" + (cat.CabinetMember ? cat.CabinetMember.LocalJobTitle : '') + "<br /><br />") +
                "Once restored, the category will again be available as an option in IRIS for facilitator use. Are you " +
                "sure you wish to continue?",
            OKText: 'Yes - Restore',
            CancelText: 'No - Cancel',
            height: '450px',
        })
            .subscribe(function (result) {
            if (result === true) {
                // User confirmed. Restore it by removing the flag.
                catfg.get('ToRemove').setValue(false);
                // Splash.
                self.splashService.SnackbarSuccess('Category Restored Successfully');
            }
        });
    };
    // #endregion
    // #region Save Changes
    CategoriesComponent.prototype.SaveChanges = function () {
        var self = this;
        if (!self.CategoryForm) {
            return;
        }
        var formval = self.CategoryForm.value;
        // Create a sorted list of all entries, sorted based upon the description field. Then, we will update the sort ordering numbers
        // for all non-to-be-removed entries, to ensure that they are all still in the proper alpha ordering.
        var updateIdx = 1;
        var toupdate = (formval.Categories || [])
            .sort(function (a, b) { return a.CategoryDescription.localeCompare(b.CategoryDescription); })
            .map(function (t) {
            return {
                IRISCategoryID: t.CategoryID,
                CategoryDescription: t.CategoryDescription,
                CabinetSAPID: t.CabinetMember && t.CabinetMember.SAPID,
                Changed: t.Changed,
                ToRemove: t.ToRemove,
                SortOrder: t.ToRemove !== true ? updateIdx++ : t.SortOrder,
            };
        });
        if (!!toupdate.length) {
            // Get a confirmation before proceeding.
            var numAdded = toupdate.filter(function (t) { return t.IRISCategoryID == null; }).length || 0;
            var numRemoved = toupdate.filter(function (t) { return t.ToRemove; }).length || 0;
            var numChanged = toupdate.filter(function (t) { return t.Changed && !t.ToRemove && t.IRISCategoryID != null; }).length || 0;
            self.dialogService
                .OpenConfirmationDialog({
                TitleText: 'Update Categories Confirmation',
                MessageText: "You are about to process the following updates to categories in the IRIS System:<br /><br />" +
                    (numAdded > 0
                        ? "<strong>" + numAdded + " new </strong>" + (numAdded === 1 ? 'category' : 'categories') + " <strong>added</strong><br />"
                        : '') +
                    (numRemoved > 0
                        ? "<strong>" + numRemoved + " existing </strong>" + (numRemoved === 1 ? 'category' : 'categories') + " <strong>removed</strong><br />"
                        : '') +
                    (numChanged > 0
                        ? "<strong>" + numChanged + " existing </strong>" + (numChanged === 1 ? 'category' : 'categories') + " <strong>modified</strong><br />"
                        : '') +
                    "<br />Once this operation is complete, all changes listed above will take effect in the IRIS System. " +
                    "Are you sure you wish to proceed?",
                OKText: 'Yes - Update All',
                CancelText: 'No - Cancel',
            })
                .subscribe(function (result) {
                if (result === true) {
                    // Go ahead with the update.
                    self.splashService
                        .OpenSplash({ TitleText: 'Please Wait', MessageText: 'Updating Categories...' })
                        .subscribe(function () {
                        self.appService.UpsertCategories(self.User.AuthToken, self.User.CurrentRole, toupdate).subscribe(function (categories) {
                            self.splashService.CloseSplash();
                            // Splash success
                            self.splashService.SnackbarSuccess('Categories Updated Successfully');
                            // Update all categories
                            self.ApplyCategorySetup(categories);
                        }, function (error) { return self.errorService.HandleServiceError(error); });
                    });
                }
            });
        }
    };
    // #endregion
    // #region Setup Tracking Method
    CategoriesComponent.prototype.SetupTracking = function () {
        var self = this;
        self.unsavedChangesService.SetTracking(self.CanDeactivate.bind(self));
    };
    // #endregion
    // #region Show Category Form
    CategoriesComponent.prototype.ShowCategoryForm = function () {
        var self = this;
        self.CategoryPanelShowing = true;
        if (self.descriptionField) {
            window.setTimeout(function () {
                self.descriptionField.nativeElement.focus();
            }, 0);
        }
    };
    // #endregion
    // #region Take Down Tracking Method
    CategoriesComponent.prototype.TakeDownTracking = function () {
        this.unsavedChangesService.ClearTracking();
    };
    // #endregion
    // #region Update Changes Flag
    CategoriesComponent.prototype.UpdateChangesFlag = function (cat) {
        if (!cat) {
            return;
        }
        cat.Changed =
            cat.OriginalCategoryDescription !== cat.CategoryDescription ||
                (!!cat.CabinetMember && !cat.OriginalCabinetMember) ||
                (!cat.CabinetMember && !!cat.OriginalCabinetMember) ||
                cat.CabinetMember.SAPID !== cat.OriginalCabinetMember.SAPID;
    };
    return CategoriesComponent;
}());
export { CategoriesComponent };
